import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { OAuthClientsService } from '../../../services/oauth/oauth-clients.service';
import { CreateOAuthClientResponse } from '../../../dto/oauth/oauth-client.dto';

@Component({
  selector: 'app-duna-oauth-clients-create-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <h2 mat-dialog-title>Opret OAuth-klient</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Navn</mat-label>
          <input matInput formControlName="name" autocomplete="off" />
          @if (form.get('name')?.hasError('required') && form.get('name')?.touched) {
          <mat-error>Navn er påkrævet</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Client ID (valgfri)</mat-label>
          <input
            matInput
            formControlName="client_id"
            placeholder="fx sim"
            autocomplete="off"
          />
          <mat-hint>Tom = auto-genereret ID</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Product key (valgfri)</mat-label>
          <input matInput formControlName="product_key" autocomplete="off" />
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Redirect URIs</mat-label>
          <textarea
            matInput
            formControlName="redirect_uris"
            rows="4"
            placeholder="Én URI per linje"
          ></textarea>
          @if (
            form.get('redirect_uris')?.hasError('required') &&
            form.get('redirect_uris')?.touched
          ) {
          <mat-error>Mindst én redirect URI</mat-error>
          }
        </mat-form-field>

        @if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="saving" (click)="cancel()">
        Annuller
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="saving || form.invalid"
        (click)="submit()"
      >
        @if (saving) {
        <mat-spinner diameter="20"></mat-spinner>
        } @else {
        Opret
        }
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .form {
        display: flex;
        flex-direction: column;
        min-width: min(440px, 90vw);
      }
      .full-width {
        width: 100%;
      }
      .error {
        color: var(--mat-sys-error, #b00020);
        margin: 0;
      }
      mat-dialog-actions button mat-spinner {
        display: inline-block;
      }
    `,
  ],
})
export class DunaOAuthClientsCreateDialogComponent implements OnDestroy {
  form: FormGroup;
  saving = false;
  errorMessage: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<
      DunaOAuthClientsCreateDialogComponent,
      CreateOAuthClientResponse | undefined
    >,
    private oauthClients: OAuthClientsService
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      client_id: [''],
      product_key: [''],
      redirect_uris: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  submit(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }
    const redirectUris = this.parseRedirectUris(
      this.form.value.redirect_uris as string
    );
    if (redirectUris.length === 0) {
      this.errorMessage = 'Angiv mindst én redirect URI.';
      return;
    }

    const clientId = (this.form.value.client_id as string)?.trim();
    const productKey = (this.form.value.product_key as string)?.trim();

    this.saving = true;
    this.errorMessage = null;
    this.oauthClients
      .create({
        name: (this.form.value.name as string).trim(),
        redirect_uris: redirectUris,
        generate_secret: true,
        ...(clientId ? { client_id: clientId } : {}),
        ...(productKey ? { product_key: productKey } : {}),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        next: (res) => this.dialogRef.close(res),
        error: (err) => {
          this.errorMessage = this.formatError(err, 'Kunne ikke oprette klient.');
        },
      });
  }

  private parseRedirectUris(text: string): string[] {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  private formatError(error: unknown, fallback: string): string {
    const httpErr = error as { error?: unknown; message?: string };
    if (typeof httpErr?.error === 'string') {
      const raw = httpErr.error.trim();
      if (raw.startsWith('<')) {
        const match = raw.match(/Fatal Error:<\/b>\s*([^<\n]+)/i);
        if (match?.[1]) {
          return match[1].trim();
        }
        return fallback;
      }
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.message) return String(parsed.message);
        if (parsed?.detail) return String(parsed.detail);
      } catch {
        return raw.length > 200 ? `${raw.slice(0, 200)}…` : raw;
      }
    }
    if (httpErr?.error && typeof httpErr.error === 'object') {
      const body = httpErr.error as { message?: string; detail?: string };
      if (body.message) return body.message;
      if (body.detail) return body.detail;
    }
    return fallback;
  }
}
